import { useMutation } from '@tanstack/react-query'
import { productImportUrlApi, type ImportProductFromUrlResponse } from 'services'

type UseImportProductFromUrlOptions = {
  onSuccess?: (data: ImportProductFromUrlResponse) => void
  onError?: (error: Error) => void
}

/**
 * VRG-353 — read a listing URL and return field values for the product form.
 *
 * Intentionally does NOT invalidate the products cache: this endpoint creates
 * nothing. The user reviews the extracted values in the form and saves, and it
 * is that save which changes the catalogue.
 */
export const useImportProductFromUrl = (options?: UseImportProductFromUrlOptions) => {
  const mutation = useMutation<ImportProductFromUrlResponse, Error, string>({
    mutationFn: productImportUrlApi.importFromUrl,

    // Both of these override QueryProvider's global mutation defaults
    // (`retry: 1`, `networkMode: 'online'`), deliberately.
    //
    // `retry: false` — a failure here is a verdict on the URL the user typed:
    // it is malformed, unreachable, or points somewhere the SSRF guard refuses.
    // None of that changes on a second attempt, so a retry only delays the
    // error by `retryDelay` (2s) and sends a second probe at the target — which
    // is exactly what we should not do for an address the guard just rejected.
    //
    // `networkMode: 'always'` — with the global default, the retry above was
    // PAUSED rather than run, leaving the mutation `isPending` forever and the
    // button stuck on "Reading page" with no error ever shown. Found by driving
    // the real flow; typecheck and unit tests cannot see it. Belt-and-braces
    // with `retry: false`, so this can't resurface if the retry default changes.
    retry: false,
    networkMode: 'always',

    onSuccess: (data) => options?.onSuccess?.(data),
    onError: (error) => options?.onError?.(error),
  })

  return {
    importFromUrl: mutation.mutate,
    importFromUrlAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  }
}
